import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import {
  FormErrors,
  LoadingEvent,
  UnSavedChangesDialog,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { toMediumDate } from '@utils/Dated';
import { Box, FormLabel } from '@mui/material';
import { FormDebugProvider } from '@ui-framework/components/AdvancedForms/FormDebugProvider';
import {
  IconLibrary,
  useAvailabilitySlotTypes,
} from '@product-framework/index';
import { FormattedMessage } from 'react-intl';
import { DialogCloseButton } from '@product-framework/components/Dialogs';
import { FormProvider, useForm } from 'react-hook-form';
import TimeSlotsSelector from '../TimeSlotsSelector';

function ScheduleUpdateDialog({
  defaultValues,
  open,
  onClose,
  serviceProviderId,
  locationId = null,
}) {
  const { formatMessage } = useLocalization();
  const { slotTypes } = useAvailabilitySlotTypes();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put } = useDataProvider(provider);
  const [unsaved, setUnsaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const dated = defaultValues?.date;

  const methods = useForm({
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const compareSources = (source1, source2) => {
    const set1 = new Set(source1);
    const set2 = new Set(source2);

    return {
      added: source2.filter(item => !set1.has(item)),
      removed: source1.filter(item => !set2.has(item)),
      unchanged: source1.filter(item => set2.has(item)),
    };
  };

  const saveSlotChanges = async ({ added = [], removed = [] }) => {
    const slots = [
      ...added.map(slotTypeCode => ({ slotTypeCode, type: 'OPEN' })),
      ...removed.map(slotTypeCode => ({ slotTypeCode, type: 'CLOSED' })),
    ];

    if (slots.length === 0) {
      return null;
    }

    const locationParam = locationId ? `/location/${locationId}` : '';

    const url = `${getApiUrl()}/sxcl/v1/availability/service-providers/${serviceProviderId}${locationParam}/special-schedules/live-calendar`;
    const response = await put(url, { startDate: dated, slots });

    return response?.ok ? null : response?.errors;
  };

  const onSubmit = async data => {
    setLoading(true);
    let currentErrors = [];
    const source1 = defaultValues.slotsToUpdate.map(s => s.code);
    const source2 = data.slotsToUpdate.map(s => s.code);
    const comparedResult = compareSources(source1, source2);
    const errorsReturned = await saveSlotChanges(comparedResult);
    if (errorsReturned) {
      currentErrors = [...currentErrors, ...errorsReturned];
    }
    if (currentErrors?.length > 0) {
      setErrors(currentErrors);
    } else {
      const newDefaultValues = {
        date: dated,
        slotsToUpdate: slotTypes.filter(s => source2.includes(s.code)),
      };
      methods.reset(newDefaultValues);
      snackbar.showMessage(
        formatMessage({
          id: 'SCHEDULE_WAS_UPDATED_SUCCESSFULLY',
          defaultMessage: 'Schedule was updated successfully',
        }),
      );
      onClose(true);
    }
    setLoading(false);
  };

  const onDialogClose = () => {
    if (isDirty) {
      setUnsaved(true);
    } else {
      onClose(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        onDialogClose();
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.LiveCalendar />
        <FormattedMessage
          id="UPDATE_SCHEDULE_FOR_DATE"
          defaultMessage="Update Schedule for {date}"
          values={{ date: toMediumDate(dated) }}
        />
        <DialogCloseButton
          disabled={loading}
          onClose={() => {
            onDialogClose();
          }}
        />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: loading ? 'none' : 'auto' }}>
          <FormDebugProvider>
            <FormProvider {...methods}>
              <FormErrors errors={errors} />
              <FormLabel>
                <FormattedMessage
                  id="SELECT_TIMESLOTS"
                  defaultMessage="Select Timeslots"
                />
              </FormLabel>
              <TimeSlotsSelector name="slotsToUpdate" />
            </FormProvider>
          </FormDebugProvider>
          <UnSavedChangesDialog
            open={unsaved}
            onLeave={() => onClose(null)}
            onStay={() => {
              setUnsaved(false);
            }}
          />
        </Box>
        <Box sx={{ mt: 2, display: !loading ? 'none' : 'auto' }}>
          <LoadingEvent>
            <FormattedMessage
              id="SAVING_SCHEDULE_UPDATES"
              defaultMessage="Saving schedule updates"
            />
            ...
          </LoadingEvent>
        </Box>
      </DialogContent>
      <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
        <Button
          disabled={!isDirty}
          type="button"
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          startIcon={<IconLibrary.Save />}>
          {formatMessage({ id: 'UPDATE', defaultMessage: 'Update' })}
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          onClick={() => methods.reset()}
          startIcon={<IconLibrary.Reset />}>
          {formatMessage({ id: 'RESET', defaultMessage: 'Reset' })}
        </Button>
        <Button
          type="button"
          onClick={() => {
            onDialogClose();
          }}
          variant="contained"
          color="secondary"
          startIcon={<IconLibrary.Close />}>
          {formatMessage({ id: 'CLOSE', defaultMessage: 'Close' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ScheduleUpdateDialog;
